import React from "react";
import { Input } from "@/components/ui/input";
import { gbp } from "@/lib/pricing";
import { PayoutLog } from "@/components/admin/PayoutLog";

// Admin editor for a trade account's white-label affiliate link. `value` is the merged
// affiliate object; `onChange(patch)` merges a partial update.
export const AffiliateAdmin = ({ d, value, onChange }) => {
  const a = value || {};
  const st = d.affiliate_stats;
  const set = (k, v) => onChange({ [k]: v });
  const tier = a.tier || (a.enabled ? "whitelabel" : "referral");
  const white = tier === "whitelabel";
  return (
    <div className="mt-5 border-t border-border pt-5" data-testid={`ds-affiliate-${d.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-heading text-lg font-semibold">Affiliate programme</div>
          <p className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            Their customers pay retail; they earn the retail − trade difference (or a %) on every paid order
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid={`ds-affiliate-enabled-${d.id}`} checked={!!a.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-primary" />
          Enabled
        </label>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid={`ds-affiliate-tier-${d.id}`}>
        {[["referral", "Referral link — DreamLabs branding", "Their link is dreamlabs.uk/p/<name>. Customers see DreamLabs, DL- order numbers, DreamLabs emails, support and payment page. They just earn commission."],
          ["whitelabel", "White-label shop — their own branding", "Own subdomain shop, own name/logo/colours, own order prefix, branded emails, own support contact, optional own BTCPay store and PGP key."]].map(([val, title, desc]) => (
          <label key={val} className={`flex cursor-pointer items-start gap-3 border p-3 text-sm transition-colors ${tier === val ? "border-primary bg-primary/10" : "border-border hover:border-foreground"}`}>
            <input type="radio" name={`tier-${d.id}`} data-testid={`ds-affiliate-tier-${val}-${d.id}`} checked={tier === val} onChange={() => set("tier", val)} className="mt-0.5 accent-primary" />
            <span><span className="block font-semibold">{title}</span><span className="block text-[11px] text-muted-foreground">{desc}</span></span>
          </label>
        ))}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Link name</label>
          <div className="mt-1 flex items-center gap-1">
            <span className="mono text-xs text-muted-foreground">/p/</span>
            <Input data-testid={`ds-affiliate-slug-${d.id}`} value={a.slug ?? ""} placeholder={d.username} onChange={(e) => set("slug", e.target.value.toLowerCase())} className="rounded-none mono" />
          </div>
        </div>
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{white ? "Shop name shown to their customers" : "Partner name (shown only to you and them)"}</label>
          <Input data-testid={`ds-affiliate-brand-${d.id}`} value={a.brand_name ?? ""} placeholder={d.name || d.username} onChange={(e) => set("brand_name", e.target.value)} className="mt-1 rounded-none" />
        </div>
{white && (<>
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Tagline</label>
          <Input data-testid={`ds-affiliate-tagline-${d.id}`} value={a.tagline ?? ""} onChange={(e) => set("tagline", e.target.value)} className="mt-1 rounded-none" />
        </div>
</>)}
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Commission</label>
          <div className="mt-1 flex items-center gap-2">
            <select data-testid={`ds-affiliate-mode-${d.id}`} value={a.commission_mode || "margin"} onChange={(e) => set("commission_mode", e.target.value)} className="rounded-none border border-border bg-background p-2 text-sm">
              <option value="margin">Retail − their trade price</option>
              <option value="percent">% of retail sale</option>
            </select>
            {(a.commission_mode || "margin") === "percent" && (
              <Input data-testid={`ds-affiliate-pct-${d.id}`} type="number" min="0" max="100" step="0.5" value={a.commission_pct ?? 0} onChange={(e) => set("commission_pct", e.target.value)} className="w-24 rounded-none mono" />
            )}
          </div>
        </div>
{white && (<>
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Logo URL</label>
          <Input data-testid={`ds-affiliate-logo-${d.id}`} value={a.logo_url ?? ""} placeholder="they can upload one from their dashboard" onChange={(e) => set("logo_url", e.target.value)} className="mt-1 rounded-none mono text-xs" />
        </div>
        <div className="sm:col-span-2">
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Scrolling top bar text (blank = no bar on their shop)</label>
          <Input data-testid={`ds-affiliate-ticker-${d.id}`} value={a.ticker_text ?? ""} maxLength={200} placeholder="Free UK shipping over £160 · Crypto only" onChange={(e) => set("ticker_text", e.target.value)} className="mt-1 rounded-none" />
        </div>
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Accent colour (hex)</label>
          <Input data-testid={`ds-affiliate-accent-${d.id}`} value={a.accent_color ?? ""} placeholder="#7c3aed" onChange={(e) => set("accent_color", e.target.value)} className="mt-1 w-32 rounded-none mono" />
        </div>
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Order number prefix</label>
          <div className="mt-1 flex items-center gap-2">
            <Input data-testid={`ds-affiliate-prefix-${d.id}`} value={a.order_prefix ?? ""} placeholder="DL" maxLength={5} onChange={(e) => set("order_prefix", e.target.value.toUpperCase())} className="w-24 rounded-none mono uppercase" />
            <span className="mono text-xs text-muted-foreground">e.g. LU → LU-9F2K7A1C</span>
          </div>
        </div>
</>)}
      </div>

{white && (<>
      <div className="mt-5 border border-border p-4" data-testid={`ds-affiliate-btcpay-${d.id}`}>
        <div className="font-heading text-sm font-semibold">Own BTCPay store (hides the main brand on the payment page)</div>
        <p className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
          Create a store for them in BTCPay, brand it, add a Greenfield API key (view + create + modify invoices) and a webhook to your API. Leave blank to use the main store.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Store ID</label>
            <Input data-testid={`ds-affiliate-store-${d.id}`} value={a.btcpay_store_id ?? ""} onChange={(e) => set("btcpay_store_id", e.target.value.trim())} className="mt-1 rounded-none mono text-xs" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">API key {a.btcpay_api_key_set && !a.btcpay_api_key && <span className="text-primary">· saved</span>}</label>
            <Input data-testid={`ds-affiliate-apikey-${d.id}`} type="password" autoComplete="off" value={a.btcpay_api_key ?? ""} placeholder={a.btcpay_api_key_set ? "•••••••• (leave blank to keep)" : ""} onChange={(e) => set("btcpay_api_key", e.target.value.trim())} className="mt-1 rounded-none mono text-xs" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Webhook secret {a.btcpay_webhook_secret_set && !a.btcpay_webhook_secret && <span className="text-primary">· saved</span>}</label>
            <Input data-testid={`ds-affiliate-whsecret-${d.id}`} type="password" autoComplete="off" value={a.btcpay_webhook_secret ?? ""} placeholder={a.btcpay_webhook_secret_set ? "•••••••• (leave blank to keep)" : ""} onChange={(e) => set("btcpay_webhook_secret", e.target.value.trim())} className="mt-1 rounded-none mono text-xs" />
          </div>
        </div>
        {a.pay_host && <p className="mono mt-2 text-[11px] text-muted-foreground">Their customers will see the payment page at <span className="text-foreground">{a.pay_host}</span></p>}
      </div>

      <div className="mt-4 border border-border p-4" data-testid={`ds-affiliate-pgp-${d.id}`}>
        <div className="font-heading text-sm font-semibold">PGP public key shown to their customers</div>
        <p className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
          Paste an ASCII-armoured PUBLIC key (never the private key). Blank = their shop offers the temp.pm link only. Whoever holds the matching private key can read the addresses.
        </p>
        <textarea data-testid={`ds-affiliate-pgp-key-${d.id}`} value={a.pgp_public_key ?? ""} rows={a.pgp_public_key ? 6 : 3} spellCheck={false}
          placeholder={"-----BEGIN PGP PUBLIC KEY BLOCK-----\n...\n-----END PGP PUBLIC KEY BLOCK-----"}
          onChange={(e) => set("pgp_public_key", e.target.value)}
          className="mt-3 w-full rounded-none border border-border bg-background p-2 font-mono text-[11px]" />
        {a.pgp_fingerprint && (
          <p className="mono mt-2 text-[11px] text-muted-foreground" data-testid={`ds-affiliate-pgp-fpr-${d.id}`}>
            Fingerprint <span className="text-foreground">{a.pgp_fingerprint}</span>{a.pgp_uid ? <> · {a.pgp_uid}</> : null}
          </p>
        )}
      </div>

      <div className="mt-4 border border-border p-4" data-testid={`ds-affiliate-support-${d.id}`}>
        <div className="font-heading text-sm font-semibold">Customer support contact (their customers contact them, not you)</div>
        <p className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Footer, order/tracking pages and order emails on their shop. They can edit these in their portal too.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Telegram username</label>
            <Input data-testid={`ds-affiliate-support-telegram-${d.id}`} value={a.support_telegram ?? ""} onChange={(e) => set("support_telegram", e.target.value)} className="mt-1 rounded-none mono text-xs" placeholder="@lulusupport" /></div>
          <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Support email</label>
            <Input data-testid={`ds-affiliate-support-email-${d.id}`} value={a.support_email ?? ""} onChange={(e) => set("support_email", e.target.value)} className="mt-1 rounded-none mono text-xs" /></div>
          <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Contact link (https://…)</label>
            <Input data-testid={`ds-affiliate-support-url-${d.id}`} value={a.support_url ?? ""} onChange={(e) => set("support_url", e.target.value)} className="mt-1 rounded-none mono text-xs" /></div>
        </div>
      </div>

</>)}
      <div className="mt-4 border border-border p-4" data-testid={`ds-affiliate-payout-${d.id}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-heading text-sm font-semibold">Automatic commission payouts (BTCPay)</div>
            <p className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              Each paid referred order sends their cut to their address in the coin the customer paid, as a pre-approved payout on the store that received the money. Needs a hot wallet + the on-chain payout processor in BTCPay, and an API key with "manage payouts / create pull payments".
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid={`ds-affiliate-autopay-${d.id}`} checked={!!a.auto_payout_enabled} onChange={(e) => set("auto_payout_enabled", e.target.checked)} className="h-4 w-4 accent-primary" />
            Auto payout on
          </label>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Network-fee allowance £ (taken off their cut)</label>
            <Input data-testid={`ds-affiliate-payout-fee-${d.id}`} type="number" min="0" max="50" step="0.25" value={a.payout_fee_gbp ?? 0} onChange={(e) => set("payout_fee_gbp", e.target.value)} className="mt-1 w-28 rounded-none mono" />
          </div>
          {["BTC", "LTC"].map((cur) => (
            <div key={cur}>
              <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{cur} address (they set this in their portal)</label>
              <Input data-testid={`ds-affiliate-payout-addr-${cur}-${d.id}`} value={(a.payout_addresses || {})[cur] ?? ""} spellCheck={false}
                onChange={(e) => set("payout_addresses", { ...(a.payout_addresses || {}), [cur]: e.target.value.trim() })} className="mt-1 rounded-none mono text-xs" placeholder="not set" />
            </div>
          ))}
        </div>
      </div>

      {a.enabled && a.slug && (
        <div className="mono mt-3 space-y-1 text-xs text-muted-foreground">
          {white && a.shop_url && a.shop_url !== a.link && <p data-testid={`ds-affiliate-shop-url-${d.id}`}>Their shop: <span className="text-foreground">{a.shop_url}</span></p>}
          {a.link && <p data-testid={`ds-affiliate-link-${d.id}`}>Referral link: <span className="text-foreground">{a.link}</span></p>}
          {white && a.sender_email && <p>Their emails go out as <span className="text-foreground">{a.brand_name || d.name || d.username} &lt;{a.sender_email}&gt;</span></p>}
          {!white && <p>DreamLabs-branded: customers on their link see the normal shop, DL- order numbers and DreamLabs emails.</p>}
        </div>
      )}
      {st && (
        <div className="mt-3 flex flex-wrap gap-2 text-[11px]" data-testid={`ds-affiliate-stats-${d.id}`}>
          <span className="border border-border px-2 py-1">Referred orders: <b>{st.orders}</b></span>
          <span className="border border-border px-2 py-1">Paid sales: <b>{gbp(st.paid_sales)}</b></span>
          <span className="border border-border px-2 py-1">Commission earned: <b>{gbp(st.commission_earned)}</b></span>
          <span className="border border-border px-2 py-1">Pending: <b>{gbp(st.commission_pending)}</b></span>
        </div>
      )}
      {a.enabled && a.slug && <PayoutLog partnerId={d.id} />}
    </div>
  );
};
